import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View
} from 'react-native';
import Autocomplete from 'react-native-autocomplete-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import EngagementLimitDialog from '@/components/EngagementLimitDialog';
import Info from '@/components/Info';
import CrossPlatformMapView from '@/components/MapView';
import { SyncStatusIndicator } from '@/components/SyncStatusIndicator';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundEngagements, useBackgroundMyActiveEngagements, useBackgroundPollingStations, useBackgroundTellingVotersByEngagements } from '@/hooks/useBackgroundFetch';
import { useCampaignContext } from '@/hooks/useCampaignContext';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { PollingStation, PollingStationInfo } from '@/types/polling_station';
import { authenticatedFetch } from '@/utils/auth';

type Voter = {
  voter_id: string;
  eno: string | number;
  eno_prefix?: string;
  name: string;
  house_number?: string;
  house_name?: string;
  street_sub_name?: string;
  street_name?: string;
  voted: boolean;
  voted_yet?: boolean;
};

const ListHeader = ({ borderColor, textColor }: { borderColor: string; textColor: string }) => (
  <View style={[AppStyles.listHeader, { borderBottomColor: borderColor }]}>
    <View style={AppStyles.enoHeaderContainer}>
      <Text style={[AppStyles.headerLabel, { minWidth: 150, color: textColor }]}>Number</Text>
    </View>
    <View style={AppStyles.voterInfoHeaderContainer}>
      <Text style={[AppStyles.headerLabel, { color: textColor }]}>Voter Details</Text>
    </View>
  </View>
);

export default function TellingEngagementPage() {
  // NOTE: engagementId parameter is now actually a polling station ID
  const { battlePlanId, pollingStationId } = useLocalSearchParams<{ battlePlanId: string; pollingStationId: string }>();
  const { getSelectedBranch, getSelectedCampaign, getElectionForCampaign } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  const selectedElection = selectedCampaign ? getElectionForCampaign(selectedCampaign, selectedBranch?.branch_short_name || '') : null;
  
  // Theme-aware colors using useThemeColor hook
  const backgroundColor = useThemeColor({}, 'background');
  const borderColor = useThemeColor({}, 'border' as any);
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const tintColor = useThemeColor({}, 'tint');
  const iconColor = useThemeColor({}, 'icon');
  const textColor = useThemeColor({}, 'text');
  const secondaryTextColor = useThemeColor({}, 'secondaryText' as any);
  const inputBackground = useThemeColor({}, 'inputBackground' as any);
  const inputBorder = useThemeColor({}, 'inputBorder' as any);
  const errorTextColor = useThemeColor({}, 'errorText' as any);
  const successTextColor = useThemeColor({}, 'successText' as any);
  const modalBackground = useThemeColor({}, 'formBackground' as any);
  const { updateTellingData } = useCampaignContext();
  const { fetchMyActiveEngagements } = useCampaignStore();
  const [actionError, setActionError] = useState<string | null>(null);
  const [showPollingDayError, setShowPollingDayError] = useState(false);

  const [isUserInAllEngagements, setIsUserInAllEngagements] = useState(false);
  
  // Fetch polling stations to get the polling station data
  const { data: pollingStations } = useBackgroundPollingStations(battlePlanId || '');
  
  // Get the polling station by ID
  const pollingStation = useMemo(() => {
    if (!pollingStations || !pollingStationId) return null;
    return pollingStations.find((ps: PollingStation) => ps.polling_station_id.toString() === pollingStationId);
  }, [pollingStations, pollingStationId]);
  
  // Extract all engagement IDs from the polling station
  const engagementIds = useMemo(() => {
    if (!pollingStation?.polling_station_districts?.length) return null;
    return pollingStation.polling_station_districts.map((district: { engagement_id: string }) => district.engagement_id);
  }, [pollingStation]);
  
  // Use background fetch hook for telling voters - now fetching for ALL engagements in the polling station
  const { data: voters, loading, error: fetchError, refetch: refetchTellingVoters } = useBackgroundTellingVotersByEngagements(isUserInAllEngagements ? engagementIds : null);
  
  // Use background fetch hook for all engagements
  const { data: allEngagements, error: allEngagementsError, refetch: refetchAllEngagements } = useBackgroundEngagements(battlePlanId || '');
  
  // Use background fetch hook for my active engagements
  const { data: myActiveEngagements, error: myEngagementsError, refetch: refetchMyActiveEngagements } = useBackgroundMyActiveEngagements();
  
 

  
  // Join/Leave state
  const [leaving, setLeaving] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [showEngagementLimitDialog, setShowEngagementLimitDialog] = useState(false);
  
  // Filter and search state - MUST be declared before any early returns
  const [voterNumber, setVoterNumber] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [streetInput, setStreetInput] = useState('');
  const [allStreets, setAllStreets] = useState<string[]>([]);
  const [hideSuggestions, setHideSuggestions] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedVoter, setSelectedVoter] = useState<Voter | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'list' | 'map' | 'info'>('list');
  
  // Map region and polygons calculation

  const router = useRouter();
  const pathname = usePathname();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  const bottomTabHeight = 48 + 12 + insets.bottom;
 
  // Derive error state from hook errors and action errors
  const error = React.useMemo(() => {
    if (actionError) return actionError;
    if (allEngagementsError) return `Failed to load engagements: ${allEngagementsError.message}`;
    if (myEngagementsError) return `Failed to load my engagements: ${myEngagementsError.message}`;
    if (fetchError) return `Failed to load engagement addresses: ${fetchError.message}`;
    return null;
  }, [actionError, allEngagementsError, myEngagementsError, fetchError]);

  const checkIfUserInEngagement = async () => {
    if (!allEngagements || !myActiveEngagements || !engagementIds || engagementIds.length === 0) return;

    // Check if user is in ALL of the polling station's engagements
    const userEngagementIds = new Set(myActiveEngagements.map((eng: any) => eng.engagement_id));
    const isUserInAll = engagementIds.every((id: string) => userEngagementIds.has(id));
    
    if (isUserInAll) {
      // Activate all engagements
      for (const engId of engagementIds) {
        const engagement = allEngagements.find((eng: any) => eng.engagement_id === engId);
        if (engagement) {
          await checkAndActivateEngagement(engagement);
        }
      }
      setActiveTab('list');
      setIsUserInAllEngagements(true);
    } else {
      setActiveTab('info');
      setIsUserInAllEngagements(false);
    }
  }

  useEffect(() => {
    checkIfUserInEngagement();
  }, [allEngagements, myActiveEngagements, engagementIds]);

  // Update streets when voters data changes
  React.useEffect(() => {
    if (voters && voters.length > 0) {      
      // Prepare street items for autocomplete
      const allStreets = Array.from(
        new Set(voters?.map((v: Voter) => v.street_name || '').filter((s: string) => s.trim() !== '') || [])
      ) as string[];
      setAllStreets(allStreets);
    }
  }, [voters]);
  
  // Calculate map region centered on polling station
  const initialRegion = React.useMemo(() => {
    if (!pollingStation?.latitude || !pollingStation?.longitude) return null;
    
    return {
      latitude: pollingStation.latitude,
      longitude: pollingStation.longitude,
      latitudeDelta: 0.01, // Small delta for close-up view
      longitudeDelta: 0.01,
    };
  }, [pollingStation]);
  

  // Create polling station markers for the new polling station markers prop
  const pollingStationMarker = React.useMemo(() => {
    if (!pollingStation?.latitude || !pollingStation?.longitude) return [];
    
    return [{
      id: `polling-station-${pollingStation.polling_station_id}`,
      coordinate: {
        latitude: pollingStation.latitude,
        longitude: pollingStation.longitude,
      },
      address: pollingStation.address,
      postcode: pollingStation.postcode,
    }];
  }, [pollingStation]);

  // Create PollingStationInfo object for the Info component
  const pollingStationInfo: PollingStationInfo | null = React.useMemo(() => {
    if (!pollingStation) return null;
    
    return {
      polling_station: {
        polling_station_id: pollingStation.polling_station_id,
        station_name: pollingStation.station_name,
        address: pollingStation.address,
        postcode: pollingStation.postcode,
        latitude: pollingStation.latitude,
        longitude: pollingStation.longitude,
        coordinates: `POINT(${pollingStation.longitude} ${pollingStation.latitude})`,
      },
      election_id: '', // Not available in PollingStation type
      polling_district_id: '', // Not available in PollingStation type
    };
  }, [pollingStation]);


  // Helper to check and activate engagement if needed
  const checkAndActivateEngagement = async (engagementData: any): Promise<void> => {
    try {
      // Check if engagement status is "open" and needs to be set to "active"
      if (engagementData.status === 'open') {
        const activateRes = await authenticatedFetch(
          selectedBranch?.branch_short_name,
          '/canvassing/start-engagement',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              engagement_id: engagementData.engagement_id
            }),
          },
          "groundforce"
        );
        
        if (!activateRes.ok) {
          console.warn(`Failed to activate engagement ${engagementData.engagement_id}: ${activateRes.status}`);
        } else {
          // Engagement was successfully activated, refetch engagement data
          try {
            // Refetch both all engagements and my active engagements to get updated data
            await Promise.all([
              refetchAllEngagements(),
              refetchMyActiveEngagements()
            ]);
            
            // The data will be updated in the store and available through the hooks
          } catch (refetchError) {
            console.warn('Error refetching engagement data after activation:', refetchError);
            // Don't throw error here as the activation was successful
          }
        }
      }
    } catch (err) {
      console.warn('Error activating engagement:', err);
      // Don't throw error here as this is not critical for loading voters
    }
  };

  // Helper function to check engagement limit for current branch
  const checkEngagementLimit = (): boolean => {
    const userEngagementsInBranch = myActiveEngagements || [];
    const pollingStationEngagementCount = engagementIds.length;
    const maxEngagements = 4; // Maximum engagements allowed per user
    const currentEngagements = userEngagementsInBranch.length;
    const freeSlots = maxEngagements - currentEngagements;
    
    // Check if user has enough free slots to join all engagements in the polling station
    if (freeSlots < pollingStationEngagementCount) {
      setShowEngagementLimitDialog(true);
      return true; // Not enough free slots
    }
    
    return false; // Enough free slots, can proceed
  };

  // Helper functions for engagement actions
  const handleJoinEngagement = async () => {
    // Check engagement limit before attempting to join
    if (checkEngagementLimit()) {
      return; // Dialog already shown, don't proceed
    }
    
    try {
      setJoining(true);
      
      // Join ALL engagements in the polling station
      const joinPromises = engagementIds.map((engId: string) => 
        authenticatedFetch(
          selectedBranch?.branch_short_name,
          '/canvassing/join-engagement',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              engagement_id: engId
            }),
          },
          "groundforce"
        )
      );
      
      const results = await Promise.allSettled(joinPromises);
      
      // Check if any failed
      const failures = results.filter((r: PromiseSettledResult<any>) => {
        if (r.status === 'rejected') return true;
        if (r.status === 'fulfilled' && !r.value.ok) return true;
        return false;
      });
      
      if (failures.length > 0) {
        console.warn(`Failed to join ${failures.length} out of ${engagementIds.length} engagements`);
        // Still continue if at least one succeeded
        if (failures.length === engagementIds.length) {
          throw new Error(`Failed to join all engagements in polling station`);
        }
      }
      
      // Engagements were successfully joined, refetch engagement data
      try {
        await refetchMyActiveEngagements();
      } catch (refetchError) {
        console.warn('Error refetching engagement data after join:', refetchError);
        // Don't throw error here as the join was successful
      }
      
    } catch (err) {
      console.error('Error joining engagements:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to join engagements');
    } finally {
      setJoining(false);
    }
  };

  // Common function for leaving an engagement
  const leaveEngagement = async (engagementIdToLeave: string, onSuccess?: () => void) => {
    try {
      setLeaving(engagementIdToLeave);
      const leaveRes = await authenticatedFetch(
        selectedBranch?.branch_short_name,
        '/canvassing/leave-engagement',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            engagement_id: engagementIdToLeave
          }),
        },
        "groundforce"
      );
      
      if (!leaveRes.ok) throw new Error(`Failed to leave engagement: ${leaveRes.status}`);
      
      // Clear all data for this engagement from the store
      useCampaignStore.getState().clearEngagementData(engagementIdToLeave);
      
      // Refresh active engagements
      fetchMyActiveEngagements()
      
      // Execute custom success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error leaving engagement:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to leave engagement');
    } finally {
      setLeaving(null);
    }
  };
  
  
  // Leave engagement function - leave ALL engagements in the polling station
  const handleLeaveEngagement = async () => {
    try {
      // Leave all engagements the user is in for this polling station
      const userEngagementIds = new Set(myActiveEngagements?.map((eng: any) => eng.engagement_id) || []);
      const engagementsToLeave = engagementIds.filter((id: string) => userEngagementIds.has(id));
      
      for (const engId of engagementsToLeave) {
        await leaveEngagement(engId);
      }
      
      // Navigate back to battle plan screen after leaving all
      router.back();
    } catch (err) {
      console.error('Error leaving engagements:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to leave engagements');
    }
  };

  const handleLeaveEngagementFromDialog = async (engagementIdToLeave: string) => {
    await leaveEngagement(engagementIdToLeave, async () => {
      // Close the dialog
      setShowEngagementLimitDialog(false);
      
      // Reset the leaving state to ensure clean state
      setLeaving(null);
    });
  };

  if (loading) {
    return <ThemedView style={{ flex: 1 }}><ActivityIndicator size="large" /></ThemedView>;
  }

  if (error) {
    return <ThemedView style={{ flex: 1 }}><ThemedText style={AppStyles.errorText}>{error}</ThemedText></ThemedView>;
  }

  // Filter streets based on input, but only when there is input
  const filteredStreets =
    streetInput.trim().length > 0
      ? allStreets.filter(street => street.toLowerCase().includes(streetInput.toLowerCase()))
      : [];

  // Filter voters by eno, name, and selected street, then sort by voter number
  const filteredVoters = voters
    ? voters
        .filter((v: Voter) => {
          const matchesNumber = voterNumber ? String(v.eno) === voterNumber : true;
          const matchesName = nameFilter
            ? nameFilter
                .toLowerCase()
                .split(' ')
                .filter(Boolean)
                .every(term => (v.name || '').toLowerCase().includes(term))
            : true;
          const matchesStreet = streetInput ? (v.street_name || '').toLowerCase().includes(streetInput.toLowerCase()) : true;
          return matchesNumber && matchesName && matchesStreet;
        })
        .sort((a: Voter, b: Voter) => {
          // Sort by eno (voter number) - convert to numbers for proper numeric sorting
          const enoA = typeof a.eno === 'string' ? parseInt(a.eno, 10) : a.eno;
          const enoB = typeof b.eno === 'string' ? parseInt(b.eno, 10) : b.eno;
          return enoA - enoB;
        })
    : [];

  // Helper function to check if today is polling day
  const isPollingDay = () => {
    if (!selectedElection) {
      return false;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const pollingDate = new Date(selectedElection.polling_day);
    pollingDate.setHours(0, 0, 0, 0);
    
    return today.getTime() === pollingDate.getTime();
  };

  const handleConfirmVoted = async (voter: Voter & { engagement_id?: string }, hasVoted: boolean) => {
    // Check if trying to record a vote when it's not polling day
    if (hasVoted) {
      const pollingDayCheck = isPollingDay();
      
      if (!pollingDayCheck) {
        // Reset the selectedVoter state since we're not updating
        setSelectedVoter(prev => prev ? { ...prev, voted_yet: voter.voted_yet ?? false } : null);
        // Show error in modal
        setShowPollingDayError(true);
        return;
      }
    }

    // Update the selectedVoter state to reflect the change immediately in the modal
    setSelectedVoter(prev => prev ? { ...prev, voted_yet: hasVoted } : null);

    const voterId = voter.voter_id;

    try {
      // Update the telling data using campaign context (handles immediate sync)
      // Use the engagement_id attached to the voter from the fetch
      await updateTellingData(voterId, {
        voted_yet: hasVoted,
        has_been_contacted: true,
        will_not_vote: false,
        will_vote_by_time: '',
        requested_lift: false,
        engagement_id: voter.engagement_id,
        voting_confirmation_timestamp: new Date().toISOString(),
        confirmation_type: 'telling'
      });
    } catch (error) {
      // Failure: keep UI change, and mark as pending
      console.error('Failed to update voted status, will retry later:', error);
      Alert.alert('Update Failed', 'Failed to update voted status. Please try again.');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchTellingVoters();
    } catch (error) {
      console.error('Failed to refresh telling voters:', error);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>
                {pollingStation?.station_name || `Telling`}
              </ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color="#007AFF" />
            </TouchableOpacity>
          ),
          headerRight: () => {
            const { getSelectedBranch } = useNavigationStore.getState();
            const selectedBranch = getSelectedBranch();
            
            return (
              <TouchableOpacity 
                onPress={() => router.push({ 
                  pathname: '/(main)/[branch]/share', 
                  params: { 
                    branch: selectedBranch?.branch_short_name || "", 
                    ref: pathname,
                    battlePlanId: battlePlanId,
                    pollingStationId: pollingStationId
                  } 
                })}
                style={{ 
                  padding: 8,
                  marginRight: 12,
                }}
              >
                <IconSymbol name="square.and.arrow.up" size={24} color="#007AFF" />
              </TouchableOpacity>
            );
          },
          headerBackVisible: false,
        }}
      />
      <ThemedView style={[AppStyles.container, { backgroundColor }]}>
        <View style={{ flex: 1, paddingBottom: bottomTabHeight, backgroundColor }}>
          {/* Sync Status Indicator */}
          <SyncStatusIndicator showDetails={true} />
          
          {/* Content based on active tab */}
          {activeTab === 'list' && isUserInAllEngagements && (
            <>
              <View style={[AppStyles.filterGroupCompact, { 
                borderBottomColor: borderColor, 
                zIndex: 1000 
              }]}>
                <View style={AppStyles.filterRow}>
                  {/* Number Filter */}
                  <View style={AppStyles.filterItem}>
                    <TextInput
                      style={[AppStyles.textInput, {
                        backgroundColor: inputBackground,
                        borderColor: inputBorder,
                        color: textColor
                      }]}
                      value={voterNumber}
                      onChangeText={text => setVoterNumber(text.replace(/[^0-9]/g, ''))}
                      placeholder="Voter no."
                      placeholderTextColor={secondaryTextColor}
                      keyboardType="numeric"
                    />
                  </View>
                  {/* Name Filter */}
                  <View style={AppStyles.filterItem}>
                    <TextInput
                      style={[AppStyles.textInput, {
                        backgroundColor: inputBackground,
                        borderColor: inputBorder,
                        color: textColor
                      }]}
                      value={nameFilter}
                      onChangeText={setNameFilter}
                      placeholder="Voter name"
                      placeholderTextColor={secondaryTextColor}
                      autoCapitalize="words"
                    />
                  </View>
                </View>
                <View style={AppStyles.filterRow}>
                  {/* Street Filter */}
                  <View style={[AppStyles.filterItem, { zIndex: 1 }]}>
                    <Autocomplete
                      data={filteredStreets}
                      value={streetInput}
                      hideResults={hideSuggestions || filteredStreets.length === 0}
                      onChangeText={text => {
                        setStreetInput(text);
                        setHideSuggestions(false);
                      }}
                      placeholder="Street name"
                      containerStyle={AppStyles.autocompleteContainer}
                      inputContainerStyle={[AppStyles.autocompleteInputContainer, {
                        backgroundColor: inputBackground,
                        borderColor: inputBorder
                      }]}
                      style={[AppStyles.autocompleteInput, { color: textColor }]}
                      listContainerStyle={[AppStyles.autocompleteList, {
                        backgroundColor: inputBackground,
                        borderColor: inputBorder
                      }]}
                      flatListProps={{
                        keyboardShouldPersistTaps: 'always',
                        keyExtractor: (item: string) => item,
                        renderItem: ({ item }: { item: string }) => (
                          <TouchableOpacity
                            style={[AppStyles.autocompleteItem, {
                              borderBottomColor: borderColor,
                              backgroundColor: inputBackground
                            }]}
                            onPress={() => {
                              setStreetInput(item);
                              setHideSuggestions(true);
                            }}
                          >
                            <Text style={[AppStyles.autocompleteText, { color: textColor }]}>{item}</Text>
                          </TouchableOpacity>
                        ),
                      }}
                    />
                  </View>
                  {/* Clear Button */}
                  <View style={AppStyles.filterItem}>
                    <TouchableOpacity
                      style={AppStyles.primaryButton}
                      onPress={() => {
                        setVoterNumber('');
                        setNameFilter('');
                        setStreetInput('');
                      }}
                    >
                      <ThemedText style={AppStyles.primaryButtonText}>Clear Filters</ThemedText>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
              {loading && <ActivityIndicator size="large" color={tintColor} style={{ marginTop: 20 }} />}
              {error && <Text style={[AppStyles.errorText, { color: errorTextColor }]}>{error}</Text>}
              {!loading && !error && (
                <FlatList
                  data={filteredVoters}
                  keyExtractor={item => String(item.voter_id)}
                  ListHeaderComponent={voterNumber || nameFilter || streetInput ? null : <ListHeader borderColor={borderColor} textColor={textColor} />}
                  keyboardShouldPersistTaps="always"
                  refreshing={refreshing}
                  onRefresh={handleRefresh}
                  renderItem={({ item }) => {
                    const addressParts = [];
                    const houseIdentifier = [item.house_number, item.house_name].filter(Boolean).join(' ');
                    
                    if (houseIdentifier) {
                      addressParts.push(houseIdentifier);
                    }
                    if (item.street_sub_name) {
                      addressParts.push(item.street_sub_name);
                    }
                    if (item.street_name) {
                      addressParts.push(item.street_name);
                    }
                    const address = addressParts.join(', ');

                    return (
                      <TouchableOpacity onPress={() => { setSelectedVoter(item); setIsModalVisible(true); }}>
                        <View style={[AppStyles.voterItem, { 
                          borderBottomColor: borderColor 
                        }]}>
                          <View style={AppStyles.enoContainer}>
                            <Text style={[AppStyles.enoText, { color: textColor }]}>{item.eno_prefix} {item.eno}</Text>
                          </View>
                          <View style={AppStyles.voterInfoContainer}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              {item.voted_yet && <MaterialIcons name="check-circle" size={18} color="green" style={{ marginRight: 5 }} />}
                              <Text style={[AppStyles.voterText, { color: textColor }]}>{item.name}</Text>
                            </View>
                            {address && <Text style={[AppStyles.addressText, { color: secondaryTextColor }]}>{address}</Text>}
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  }}
                  ListEmptyComponent={<ThemedText>No voters found for these filters.</ThemedText>}
                  style={{ marginTop: 10, width: '100%', backgroundColor }}
                  contentContainerStyle={{ paddingBottom: insets.bottom, backgroundColor }}
                />
              )}
            </>
          )}
          
          {activeTab === 'map' && (
            <View style={{ flex: 1, backgroundColor }}>
              {pollingStationMarker && initialRegion ? (
                <CrossPlatformMapView
                  initialRegion={initialRegion}
                  pollingStationMarkers={pollingStationMarker}
                  showUserLocation={true}
                  style={{ flex: 1, width: '100%' }}
                />
              ) : (
                <View style={[AppStyles.emptyContainer, { justifyContent: 'center', alignItems: 'center', backgroundColor }]}>
                  {!pollingStation ? (
                    <>
                      <ActivityIndicator size="large" color={tintColor} style={{ marginBottom: 16 }} />
                      <ThemedText style={{ textAlign: 'center', marginBottom: 10 }}>
                        Loading Map...
                      </ThemedText>
                      <ThemedText style={{ textAlign: 'center', opacity: 0.7 }}>
                        Fetching polling station data
                      </ThemedText>
                    </>
                  ) : (
                    <>
                      <ThemedText style={{ textAlign: 'center', marginBottom: 10 }}>
                        Polling Station Map
                      </ThemedText>
                      <ThemedText style={{ textAlign: 'center', opacity: 0.7 }}>
                        Location data is not available for this polling station
                      </ThemedText>
                    </>
                  )}
                </View>
              )}
            </View>
          )}
          
          {activeTab === 'info' && pollingStationInfo && (
            <Info 
              engagementInfo={pollingStationInfo as any}
              isUserInEngagement={isUserInAllEngagements}
              onJoinEngagement={handleJoinEngagement}
              onLeaveEngagement={handleLeaveEngagement}
              joining={joining}
              leaving={leaving !== null}
              insets={insets}
              engagementId={pollingStationId as string}
              isTellingEngagement={true}
              voters={voters || []}
              pollingStationInfo={pollingStationInfo}
            />
          )}
        </View>
        
        {/* Bottom Tab Navigation */}
        <View style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          flexDirection: 'row',
          backgroundColor: backgroundColor,
          borderTopWidth: 1,
          borderTopColor: borderColor,
          paddingBottom: insets.bottom,
        }}>
          {isUserInAllEngagements && (
            <TouchableOpacity
              style={[
                {
                  flex: 1,
                  paddingVertical: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                activeTab === 'list' && { backgroundColor: cardBackground }
              ]}
              onPress={() => setActiveTab('list')}
            >
              <MaterialIcons 
                name="list" 
                size={24} 
                color={activeTab === 'list' ? tintColor : iconColor} 
              />
              <ThemedText style={[
                { fontSize: 12, marginTop: 4 },
                activeTab === 'list' ? { color: tintColor } : { color: iconColor }
              ]}>List</ThemedText>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[
              {
                flex: 1,
                paddingVertical: 12,
                alignItems: 'center',
                justifyContent: 'center',
              },
              activeTab === 'map' && { backgroundColor: cardBackground }
            ]}
            onPress={() => setActiveTab('map')}
          >
            <MaterialIcons 
              name="map" 
              size={24} 
              color={activeTab === 'map' ? tintColor : iconColor} 
            />
            <ThemedText style={[
              { fontSize: 12, marginTop: 4 },
              activeTab === 'map' ? { color: tintColor } : { color: iconColor }
            ]}>Map</ThemedText>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              {
                flex: 1,
                paddingVertical: 12,
                alignItems: 'center',
                justifyContent: 'center',
              },
              activeTab === 'info' && { backgroundColor: cardBackground }
            ]}
            onPress={() => setActiveTab('info')}
          >
            <MaterialIcons 
              name="info" 
              size={24} 
              color={activeTab === 'info' ? tintColor : iconColor} 
            />
            <ThemedText style={[
              { fontSize: 12, marginTop: 4 },
              activeTab === 'info' ? { color: tintColor } : { color: iconColor }
            ]}>Info</ThemedText>
          </TouchableOpacity>
        </View>
      </ThemedView>
      {selectedVoter && (
        <Modal
          animationType="slide"
          transparent={true}
          visible={isModalVisible}
          onRequestClose={() => {
            setShowPollingDayError(false);
            setIsModalVisible(false);
          }}
        >
          <TouchableOpacity
            style={AppStyles.modalContainer}
            activeOpacity={1}
            onPressOut={() => {
              setShowPollingDayError(false);
              setIsModalVisible(false);
            }}
          >
            <TouchableWithoutFeedback>
              <View style={[AppStyles.modalView, { backgroundColor: modalBackground }]}>
                <TouchableOpacity
                  style={AppStyles.modalCloseButton}
                  onPress={() => {
                    setShowPollingDayError(false);
                    setIsModalVisible(false);
                  }}
                  accessibilityLabel="Close dialog"
                  accessibilityHint="Closes the voter details dialog"
                >
                  <MaterialIcons name="close" size={20} color={iconColor} />
                </TouchableOpacity>
                <Text style={[AppStyles.modalTitle, { color: textColor }]}>{selectedVoter.name}</Text>
                
                <View style={AppStyles.detailRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[AppStyles.detailLabel, { color: secondaryTextColor }]}>Voter Number</Text>
                    <Text style={[AppStyles.voterNumberValue, { color: textColor }]}>{selectedVoter.eno_prefix} {selectedVoter.eno}</Text>
                  </View>
                </View>

                <View style={AppStyles.detailRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[AppStyles.detailLabel, { color: secondaryTextColor }]}>Address</Text>
                    <Text style={[AppStyles.detailValue, { color: textColor }]}>
                      {[selectedVoter.house_number, selectedVoter.house_name, selectedVoter.street_sub_name, selectedVoter.street_name].filter(Boolean).join(', ')}
                    </Text>
                  </View>
                </View>

                <View style={[AppStyles.separator, { backgroundColor: borderColor }]} />

                {showPollingDayError && (
                  <View style={{ 
                    marginBottom: 16, 
                    marginTop: 4,
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingHorizontal: 12, 
                    backgroundColor: errorTextColor + '20', 
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: errorTextColor + '40'
                  }}>
                    <ThemedText style={[AppStyles.errorText, { textAlign: 'center', marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0, padding: 0, lineHeight: 20 }]}>
                      Cannot record a vote - it is not polling day.
                    </ThemedText>
                  </View>
                )}

                <TouchableOpacity
                  style={AppStyles.checkboxContainer}
                  onPress={() => {
                    setShowPollingDayError(false);
                    handleConfirmVoted(selectedVoter, !selectedVoter.voted_yet);
                  }}
                >
                  <MaterialIcons
                    name={selectedVoter.voted_yet ? 'check-box' : 'check-box-outline-blank'}
                    size={30}
                    color={selectedVoter.voted_yet ? successTextColor : secondaryTextColor}
                  />
                  <Text style={[AppStyles.checkboxLabel, { color: textColor }]}>Voted</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={AppStyles.primaryButton} 
                  onPress={() => {
                    setShowPollingDayError(false);
                    setIsModalVisible(false);
                  }}
                >
                  <Text style={AppStyles.primaryButtonText}>Close</Text>
                </TouchableOpacity>
              </View>
            </TouchableWithoutFeedback>
          </TouchableOpacity>
        </Modal>
      )}
      
      {/* Engagement Limit Dialog */}
      <EngagementLimitDialog
        visible={showEngagementLimitDialog}
        onClose={() => setShowEngagementLimitDialog(false)}
        myActiveEngagements={myActiveEngagements || []}
        onLeaveEngagement={handleLeaveEngagementFromDialog}
        leaving={leaving}
      />
    </KeyboardAvoidingView>
  );
} 